import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { createSmsInitialState, getSmsTemplates, isSmsPhoneNumber, normalizeSmsRecipients } from '../../utils/sms';
import { ButtonUI, DialogUI, FormLabelUI, InputUI, SelectUI } from '../Interface';
import SmsTemplateVariablePanel from '../template/SmsTemplateVariablePanel';

const DocumentSmsDialog = ({
  config, currentDocType, documentValues, onClose, onSend, open, selectedCompany, selectedPrintReport, user,
}) => {
  const templates = useMemo(() => getSmsTemplates(currentDocType, selectedPrintReport), [currentDocType, selectedPrintReport]);
  const [formState, setFormState] = useState(() => createSmsInitialState(templates, documentValues, selectedCompany));
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const bodyInputRef = useRef(null);

  useEffect(() => {
    if (open) setFormState(createSmsInitialState(templates, documentValues, selectedCompany));
  }, [open, templates, documentValues, selectedCompany]);

  const insertVariable = (token) => {
    const input = bodyInputRef.current;
    const start = input?.selectionStart ?? formState.body.length;
    const end = input?.selectionEnd ?? start;
    const insertion = `${token} `;
    setFormState((prev) => ({ ...prev, body: prev.body.slice(0, start) + insertion + prev.body.slice(end) }));
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + insertion.length, start + insertion.length);
    });
  };

  const handleSend = async () => {
    if (sendingRef.current) return;
    const smsTo = normalizeSmsRecipients(formState.to);
    if (!smsTo.length || !smsTo.every(isSmsPhoneNumber)) {
      addNotification({ content: 'Adj meg érvényes címzett telefonszámot.', type: NotificationType.WARNING });
      return;
    }
    if (!formState.body.trim()) {
      addNotification({ content: 'Az SMS szövege nem lehet üres.', type: NotificationType.WARNING });
      return;
    }
    if (!onSend) {
      addNotification({ content: 'Az SMS küldés jelenleg nem érhető el.', type: NotificationType.INFO });
      return;
    }

    sendingRef.current = true;
    setSending(true);
    try {
      await onSend({
        smsTo,
        smsFrom: config?.smsSender?.trim() || null,
        body: formState.body,
        templateId: formState.selectedTemplateId || null,
        documentTypeId: currentDocType?.documentTypeId ?? null,
        formId: selectedPrintReport?.formid ?? null,
        baseId: documentValues?.docId ?? null,
        userId: user?.userId ?? null,
      });
      addNotification({ content: 'Az SMS küldés elindult!', type: NotificationType.SUCCESS });
      onClose();
    } catch (error) {
      addNotification({ content: 'Az SMS küldése sikertelen. Próbáld újra.', type: NotificationType.ERROR });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <DialogUI open={open} onClose={() => { if (!sendingRef.current) onClose(); }} headerContent="SMS küldés indítása" maxWidth="md" fullWidth closeBtn={false}>
      <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {templates.length > 0 && (
          <SelectUI
            value={formState.selectedTemplateId}
            disabled={sending}
            option={templates.map((template) => ({ value: template.id, label: [template.name, template.languageCode].filter(Boolean).join(' - ') }))}
            label={<FormLabelUI text="Sablon" />}
            onChange={(event) => {
              const template = templates.find((item) => String(item.id) === String(event.target.value));
              setFormState((prev) => ({ ...prev, selectedTemplateId: template?.id ?? '', body: template?.body ?? '' }));
            }}
          />
        )}
        <InputUI
          type="tel"
          value={formState.to}
          onChange={(event) => setFormState((prev) => ({ ...prev, to: event.target.value }))}
          disabled={sending}
          label={<FormLabelUI text="Címzett telefonszáma" />}
          helperText="Több telefonszámot pontosvesszővel vagy vesszővel válassz el. Példa: +36301234567"
          variant="standard"
          InputLabelProps={{ shrink: Boolean(formState.to) }}
        />
        <InputUI
          value={config?.smsSender ?? ''}
          label={<FormLabelUI text="Feladó" />}
          variant="standard"
          disabled
        />
        <InputUI
          inputRef={bodyInputRef}
          value={formState.body}
          onChange={(event) => setFormState((prev) => ({ ...prev, body: event.target.value }))}
          disabled={sending}
          label={<FormLabelUI text="SMS szövege" />}
          multiline
          rows={6}
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary">
          Karakterszám: {formState.body.length}. A végleges hossz a sablonváltozók behelyettesítése után változhat.
        </Typography>
        <SmsTemplateVariablePanel disabled={sending} onInsertVariable={insertVariable} />
        <Box display="flex" gap={2} justifyContent="center" pt={1}>
          <ButtonUI text={sending ? 'Küldés...' : 'Küldés'} color="green" type="button" disabled={sending} onClick={handleSend} />
          <ButtonUI text="Mégsem" color="blue" type="button" disabled={sending} onClick={onClose} />
        </Box>
      </Box>
    </DialogUI>
  );
};

export default DocumentSmsDialog;
